import React, { PureComponent, cloneElement } from 'react'
import Immutable from 'immutable'
import { EditableComponent } from '../../components'
import { getEditableDisplayComponentKey, isWatchEditMode } from '../../components/EditableComponent/state'

class Editable extends PureComponent {
  static renderLayer = ({ childKey, ...restArgs }) => {
    const { dialType, parentKey, allAssets, allData, menu } = restArgs
    const renderKey = isWatchEditMode({ allAssets, dialType })
      ? ''
      : (childKey || Editable.getRenderComponent({ dialType, parentKey, allAssets, allData, menu }))

    // Edit mode renders component decorations, not child runtime data.
    if (!renderKey) {
      // Clear runtime entries that can make the labelText guard incorrectly
      // treat edit-mode labels as already rendered.
      const currentKey = restArgs.keyPrefix
      Object.keys(EditableComponent.keyMap).forEach((k) => {
        if (k !== currentKey) delete EditableComponent.keyMap[k]
      })
      EditableComponent.renderLayer({ ...restArgs, parentKey })
      return
    }

    // Clear edit decorations before rendering runtime data.
    const editKey = restArgs.keyPrefix
    const editKeyMap = EditableComponent.keyMap[editKey]
    if (editKeyMap) {
      editKeyMap.imageKeys.forEach(key => restArgs.onRemove(`${editKey}-${key}`))
      editKeyMap.vertexKeys.forEach(key => restArgs.onRemove(`${editKey}-${key}`))
      delete EditableComponent.keyMap[editKey]
    }

    // Render runtime child data once through EditableComponent to avoid duplicate previews.
    EditableComponent.renderLayer(restArgs)
  }

  static dealData = ({ dialType, allData, allAssets, parentKey, childKey }) => {
    if (!childKey) return { data: Immutable.Map(), assets: Immutable.Map() }
    const [component, subKey] = childKey.split('-')
    const path = parentKey.split('_')
    const data = subKey
      ? allData.getIn([...path, 'props', component, 'children', subKey]) || Immutable.Map()
      : allData.getIn([...path, 'props', component]) || Immutable.Map()
    const assets = subKey
      ? allAssets.getIn([dialType, ...path, 'props', component, 'children', subKey]) || Immutable.Map()
      : allAssets.getIn([dialType, ...path, 'props', component]) || Immutable.Map()
    return {
      data,
      assets
    }
  }

  static getRenderComponent = ({ dialType, parentKey, allAssets, allData, menu }) => {
    const path = parentKey.split('_')
    if (isWatchEditMode({ allAssets, dialType })) return ''

    return getEditableDisplayComponentKey({
      allAssets,
      dialType,
      path,
      menu,
      parentKey,
      componentData: allData?.getIn?.([...path])
    })
  }

  render() {
    const { allData, allAssets, parentKey, childKey, CONFIG_MAP, dialType, menu } = this.props
    const { componentMap, idle, normal } = CONFIG_MAP
    const nowConfig = dialType === 'normal' ? normal : idle
    const { layerConfig } = nowConfig
    if (!childKey) {
      return (
        <div className="control-container">
          <EditableComponent layerConfig={layerConfig} {...this.props} />
        </div>
      )
    }

    const [component, subKey] = childKey.split('-')
    const { instance: componentInstance, propMap = {}, constMap = Immutable.Map() } = componentMap(dialType)[component]
    // EditableComponent owns the edit-mode canvas decorations. Hide the config
    // child to prevent its updates from inserting runtime data into the canvas.
    const isHidden = isWatchEditMode({ allAssets, dialType })
      || Editable.getRenderComponent({ dialType, parentKey, allAssets, allData, menu }) !== component
    const path = parentKey.split('_')
    const { data, assets } = Editable.dealData({ childKey, allData, allAssets, parentKey, dialType })

    return cloneElement(componentInstance, {
      ...this.props,
      key: dialType,
      prefixPath: [...path, 'props'],
      keyPrefix: `${dialType}-${path[0]}_${path[1]}-${component}`,
      propMap,
      constMap,
      parentKey: component,
      editableComponentKey: parentKey,
      childKey: subKey,
      allData,
      layerConfig,
      hidden: isHidden,
      data,
      assets
    })
  }
}

export default Editable
